import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  addDeviceMapping,
  deleteDeviceMapping,
  getDeviceIncomingLastValue,
  getDeviceMappings,
  getRelayStatusLookups,
  getSwitchOnOffStateTypeLookups,
  getTemperatureTypeLookups,
  updateDeviceMapping,
  type MappingPayload,
} from "src/services/mappingService";
import { useGeneralRefetchOverride } from "./general.queries";
import { QueryNames } from "./queries.constant";

type QueryOptions = { enabled?: boolean } & Record<string, unknown>;

export const useQueryGetDeviceIncomingLastValue = (
  params?: { unitId?: string | number; brand?: string },
  options: QueryOptions = {}
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!params?.unitId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.deviceIncomingLastValue, params, language],
      queryFn: () =>
        getDeviceIncomingLastValue(params as { unitId: string | number; brand?: string }, language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useQueryGetDeviceMappings = (unitId?: string | number, options: QueryOptions = {}) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  const enabled = !!unitId && options.enabled !== false;

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.deviceMappings, unitId, language],
      queryFn: () => getDeviceMappings(unitId as string | number, language),
      enabled,
      ...options,
    }),
    enabled
  );
};

export const useMutationAddDeviceMapping = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: MappingPayload) => addDeviceMapping(payload, language),
    onSuccess: (_data, payload) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.deviceMappings, payload.unitId, language],
        exact: true,
      });
    },
  });
};

export const useMutationUpdateDeviceMapping = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: {
      id: string | number;
      unitId: string | number;
      payload: Omit<MappingPayload, "unitId">;
    }) => updateDeviceMapping(params, language),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.deviceMappings, params.unitId, language],
        exact: true,
      });
    },
  });
};

export const useMutationDeleteDeviceMapping = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (params: { id: string | number; unitId: string | number }) =>
      deleteDeviceMapping(params, language),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: [...QueryNames.deviceMappings, params.unitId, language],
        exact: true,
      });
    },
  });
};

export const useQueryGetRelayStatusLookups = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.relayStatusLookup, language],
    queryFn: () => getRelayStatusLookups(language),
    select: (data) => data.data.response,
    enabled,
  });
};

export const useQueryGetSwitchOnOffStateTypeLookups = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.switchOnOffStateTypeLookup, language],
    queryFn: () => getSwitchOnOffStateTypeLookups(language),
    select: (data) => data.data.response,
    enabled,
  });
};

export const useQueryGetTemperatureTypeLookups = (enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.temperatureTypeLookup, language],
    queryFn: () => getTemperatureTypeLookups(language),
    select: (data) => data.data.response,
    enabled,
  });
};
